/**
 * V2 观察窗内的日粒度序列归一（P1-1.7，修 P8）
 *
 * V1 的 `toSeries` 把整个 `{key,value}` 对象塞进图表的 `value`，靠 ECharts"对象项带 value 字段"
 * 的约定侥幸出图，`observed` 在搬运途中被丢掉，"缺失"分支实际不可达（feasibility §1.6 P8）。
 * V2 不复用那条路：这里把 indicators 的日粒度项统一成 `{date, value, observed}` 三态行，
 * **observed 只有 true / false / null 三种值**，null = 服务端这一天压根没给（缺位），
 * 与 false（给了但判为未采集）在页面上是两种画法：前者画空心格，后者画灰底格。
 *
 * 本文件不做任何判定：没有阈值、没有"够不够"，只有"这一格该画成什么"。
 */
import { alignSeriesToWindow } from '../window';
import { CELL_STATE_TEXT } from './copy';

/** observed 三态：只有严格 true/false 才算服务端表了态 */
function observedOf(item) {
  if (!item || typeof item !== 'object') return null;
  if (item.observed === true) return true;
  if (item.observed === false) return false;
  return null;
}

/** value 是字符串承载（与 BaseIndicatorItemModel 一致）；不可解析时返回 null，不返回 0 */
export function numericOf(item) {
  const raw = item && typeof item === 'object' ? item.value : item;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * 把服务端序列按窗口对齐成三态行。
 *
 * ⚠ 日期取**窗口位**而不是 `item.key`：服务端没给这一天时，对齐后那一位是 null，
 * 若日期也跟着丢，整格就变成“窗口外的占位”，而它其实是“上游未给出”——
 * 这两个词在页面上是两句不同的话（一个是链路问题、一个是数据没采到），不能共一个格。
 *
 * @param {Array<{key:string,value:string,observed:boolean}>} items indicators 里某个指标的逐日数组
 * @param {string[]} dayKeys resolveHabitWindow().dayKeys
 * @returns {Array<{date:string,value:number|null,observed:boolean|null}>}
 */
export function toSeries(items, dayKeys = []) {
  return alignSeriesToWindow(items, dayKeys).map((item, index) => ({
    date:
      dayKeys[index] ||
      (item && typeof item.key === 'string' ? item.key : null),
    value: numericOf(item),
    observed: observedOf(item),
  }));
}

/**
 * 画一格的视觉状态。返回值只用于挑 CSS 类，不携带任何业务结论。
 * `unreported`（服务端没给这一天）与 `uncollected`（给了但没采到）必须能区分：
 * 前者是链路问题、后者是用户行为，混在一起页面就会把"上游断更"讲成"用户没做"。
 */
export function cellState(row) {
  if (!row || row.date === null || row.date === undefined) return 'absent';
  if (row.observed === true) {
    return row.value === null ? 'uncollected' : 'observed';
  }
  if (row.observed === false) return 'uncollected';
  return 'unreported';
}

export const CELL_STATES = ['observed', 'uncollected', 'unreported', 'absent'];

/** 文字在 copy.js 的 CELL_STATE_TEXT（文案一律集中于那一处，本文件只管状态） */
export function cellText(state) {
  return CELL_STATE_TEXT[state] || CELL_STATE_TEXT.absent;
}

/** 日历格 + 数值：`observed!==true` 时不给数值（哪怕 value 是 "0"，那是哨兵，R2） */
export function cellForDisplay(row) {
  const state = cellState(row);
  return {
    date: row && row.date ? row.date : null,
    state,
    text:
      state === 'observed'
        ? formatCellValue(row.value)
        : CELL_STATE_TEXT[state],
    hasNumber: state === 'observed',
  };
}

function formatCellValue(value) {
  if (value === null || value === undefined) return '';
  return Number.isInteger(value)
    ? String(value)
    : String(Math.round(value * 10) / 10);
}

/**
 * 这一格是不是"有记录"。
 *
 * 口径以服务端为准：打卡类字段只要当天有日报行就是 `observed=true`，`value` 里那些 0 是
 * "没打卡"而不是"观测到 0"（coverage §3：拿 `observed=true` 当信号会把 93% 从未记过饮食的
 * 用户判成"饮食充分"）。服务端口径就是 `value > 0` 才算这一维的信号（`activeDays` 就按此算）。
 *
 * 本函数是 v2 里少数允许做数值比较的地方（另一个是 scale.js），与 R16 的分工：
 * 这里判的是"这个数算不算一个记录"（契约口径），不是"这个用户属于哪种情况"（D12）。
 */
export function isSignal(row) {
  return Boolean(
    row &&
      row.observed === true &&
      typeof row.value === 'number' &&
      row.value > 0,
  );
}

/**
 * 一行的窗口汇总：只累加"有记录"的天，缺一天就少一天 ——
 * 把没记的那天当 0 加进去，就是把"没记"写成"记了 0"（R2）。
 * 返回的 `count` 就是实际有记录的天数，页面上要一并写明（否则"合计 120 千卡"会被读成整窗的量）。
 */
export function signalSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let count = 0;
  let sum = 0;
  let latest = null;
  let latestDate = null;
  list.forEach((row) => {
    if (!isSignal(row)) return;
    count += 1;
    sum += row.value;
    if (row.date) {
      latest = row.value;
      latestDate = row.date;
    }
  });
  return {
    count,
    sum,
    mean: count ? sum / count : null,
    latest,
    latestDate,
  };
}

/**
 * 按指标的汇总口径给出窗口汇总。`kind` 来自 registry.DIMENSION_METRICS：
 * 次数/时长/热量/步数给 `sum`，睡眠时长给 `mean`（时长求和没有意义），心率类给 `latest`。
 */
export function aggregateOf(rows, kind) {
  const s = signalSummary(rows);
  if (!s.count) return { kind, value: null, count: 0, date: null };
  if (kind === 'mean') {
    return { kind, value: s.mean, count: s.count, date: null };
  }
  if (kind === 'latest') {
    return { kind, value: s.latest, count: s.count, date: s.latestDate };
  }
  return { kind, value: s.sum, count: s.count, date: null };
}

/**
 * 逐日槽位（天粒度）：只有"有记录"的那天带值，其余一律 null。
 * 未采集与上游未给出在展示上合并（已拍板：不做区分），所以这里只输出两态：有值 / 无值。
 */
export function dayBuckets(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    date: row && row.date ? row.date : null,
    value: isSignal(row) ? row.value : null,
  }));
}

/**
 * 按自然周切槽位（周粒度）：每 7 天一段，用该指标自己的汇总口径聚一次。
 * 一周里一天记录都没有 ⇒ `value` 为 null（不画柱），与"合计 0"是两回事。
 */
export function weekBuckets(rows, kind) {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];
  for (let i = 0; i < list.length; i += 7) {
    const chunk = list.slice(i, i + 7);
    const agg = aggregateOf(chunk, kind);
    const last = chunk[chunk.length - 1];
    out.push({
      index: out.length,
      start: chunk[0] && chunk[0].date ? chunk[0].date : null,
      end: last && last.date ? last.date : null,
      value: agg.value,
      count: agg.count,
    });
  }
  return out;
}

/**
 * 自设目标的进度（只认 `source === 'user'`）。
 *
 * 为什么卡得这么死：默认值（C4 的 `3 次/周`）与权重一样是**无外部背书的业务约定**，
 * 拿它当参照就是伪造（§5.6 第 5 条）；`none` 是没设。两者都返回 null，调用方就不画这一行。
 *
 * 与结论层的关系：这段原本写在 `conclusion.js` 里当一句结论（“你自设的目标是每周 4 次…”），
 * 2026-09-16 拍板改放卡片 —— 它是**数字**，跟均值/最长连续那些一格，而不是一句需要读完的话。
 *
 * @returns {{target:number,unit:string,actual:number,delta:number,ratio:number|null,achieved:boolean}|null}
 */
export function targetProgressOf(dim, windowDays) {
  const target = (dim && dim.selfTarget) || {};
  if (target.source !== 'user') return null;
  const targetValue = numericOf({ value: target.value });
  const days = Number(windowDays);
  const base = String(target.unit || '')
    .split('/')[0]
    .trim();
  if (targetValue === null || !base || !Number.isFinite(days) || days <= 0) {
    return null;
  }
  // 目标单位是“次/周”⇒ 找事实行里单位为“次”的那条，按周折算出实际值
  const facts = Array.isArray(dim && dim.facts) ? dim.facts : [];
  const hit = facts.find(
    (fact) => fact && fact.unit === base && fact.observed !== false,
  );
  const total = hit ? numericOf(hit) : null;
  if (total === null) return null;
  const actual = total / (days / 7);
  const delta = actual - targetValue;
  return {
    target: targetValue,
    unit: base,
    actual,
    delta,
    ratio: targetValue > 0 ? actual / targetValue : null,
    achieved: delta >= 0,
  };
}

/**
 * 三大营养素的窗口分布（饮食卡片用）：按“供能占比”而不是克数。
 *
 * `macros` 从 `registry.MACRO_METRICS` 传入（那边同时钉了 label 与每克千卡数），
 * 本函数只做求和与占比 —— 与 `isSignal`/`scale.js` 一样属于“允许算数”的地方，不做判定。
 * 一天都没记录时返回 null（调用方不画那块），而不是返回三个 0。
 *
 * @returns {Array<{key,label,unit,grams,kcal,days,share}>|null} share 是供能占比（和恒为 1）
 */
export function macroSplitOf(indicators, macros, dayKeys) {
  const list = Array.isArray(macros) ? macros : [];
  if (!list.length) return null;
  const parts = list.map((macro) => {
    const summary = signalSummary(
      primarySeriesOf(indicators, macro.series, dayKeys),
    );
    const grams = summary.sum;
    return {
      key: macro.key,
      label: macro.label,
      unit: macro.unit,
      grams,
      days: summary.count,
      kcal: grams * (macro.kcalPerGram || 0),
    };
  });
  const totalKcal = parts.reduce((acc, part) => acc + part.kcal, 0);
  if (!totalKcal) return null;
  return parts.map((part) => ({ ...part, share: part.kcal / totalKcal }));
}

/**
 * 一格的 tooltip。抽出来是因为日历与逐日矩阵都要用：两处各写一遍，
 * "上游未给出"与"未采集"迟早会在其中一处被写成同一个词。
 */
export function cellTitle(cell, { unit = '', alignHint = '' } = {}) {
  if (!cell || !cell.date) return cellText('absent');
  const detail = cell.hasNumber
    ? `${cell.text} ${unit || ''}`.trim()
    : cellText(cell.state);
  return `${cell.date}${alignHint}：${detail}`;
}

/**
 * 从 indicators 里取某维的主指标序列。
 * 序列键写在 registry.PRIMARY_METRIC[dim].series 里，那里同时钉了单位 —— 量纲与序列键必须同一处维护，
 * 否则就会出现"柱子是分钟、标签写小时"这种页面上看不出来的错（v2 最容易错的一类）。
 */
export function primarySeriesOf(indicators, seriesPath, dayKeys) {
  const parts = String(seriesPath || '').split('.');
  let node = indicators;
  for (let i = 0; i < parts.length; i += 1) {
    if (!node || typeof node !== 'object') {
      node = null;
      break;
    }
    node = node[parts[i]];
  }
  return toSeries(Array.isArray(node) ? node : [], dayKeys);
}
